<script setup>
import { reactive, watch, ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import QuickStartSetupCard from '../components/QuickStartSetupCard.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});
const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const BASE_KEY = 'AILAB.ASSISTANT_V2.GENERAL';

const isLoading = ref(true);
const isSubmitting = ref(false);

const openDeployTab = () => {
  router.replace({ query: { ...(route.query || {}), tab: 'deploy_channels' } });
};

const openTrainContentTab = () => {
  router.replace({ query: { ...(route.query || {}), tab: 'train_content' } });
};

const openSettingsTab = () => {
  router.replace({ query: { ...(route.query || {}), tab: 'settings_general' } });
};

// Get AI Closer config from store
const aiCloserConfig = computed(() => store.getters['aiCloser/getConfig']);
const uiFlags = computed(() => store.getters['aiCloser/getUIFlags']);

// Draft protection: prevent server/store refresh from overwriting unsaved edits.
const isDirty = ref(false);
const isSyncingFromStore = ref(false);
const lastSyncedSnapshot = ref(null);
const lastSyncedAssistantId = ref(null);

// Connected inboxes (AI Closer deployment)
const connectedInboxes = computed(
  () => store.getters['ailabInboxes/getRecords'] || []
);
const selectedInboxId = ref(null);
const autoResolveAfterMinutes = ref(60);
const aiOverrideMode = ref('inherit'); // inherit | suggest | auto_suggest
const aiOverrideReason = ref('');
const aiOverrideMeta = reactive({ setAt: '', setByUserId: null });

const formData = reactive({
  name: '',
});

const setupOverviewCards = computed(() => [
  {
    label: 'Assistant name',
    value: formData.name || props.assistant?.name || 'Not set yet',
    note: 'Use a clear internal name so your team can spot this AI quickly.',
  },
  {
    label: 'Connected channels',
    value: connectedInboxes.value.length,
    note: connectedInboxes.value.length
      ? 'This assistant already has live inbox connections.'
      : 'Connect inboxes after your content and policies are ready.',
  },
  {
    label: 'Save status',
    value: isDirty.value ? 'Unsaved changes' : 'Saved',
    note: isDirty.value
      ? 'Use the top Save or the button below to keep your latest basics.'
      : 'Core setup is synced with the saved assistant record.',
  },
]);

const currentDraftSnapshot = () => ({
  name: formData.name,
});

const markSynced = () => {
  lastSyncedSnapshot.value = currentDraftSnapshot();
  lastSyncedAssistantId.value = Number(props.assistant?.id) || null;
  isDirty.value = false;
};

// Load AI Closer config on mount
onMounted(async () => {
  if (props.assistant?.id) {
    try {
      // Ensure connected inboxes are available for per-inbox auto-resolve setting
      await store.dispatch('ailabInboxes/get', {
        assistantId: props.assistant.id,
      });
      // Ensure inbox records (additional_attributes) are available
      await store.dispatch('inboxes/get');
      await store.dispatch('aiCloser/fetchConfig', {
        assistantId: props.assistant.id,
      });
    } catch (error) {
      // Config might not exist yet, that's ok
      console.log('AI Closer config not found, will create on first save');
    } finally {
      isLoading.value = false;
    }
  } else {
    isLoading.value = false;
  }
});

// Sync config to form when loaded
watch(
  aiCloserConfig,
  newConfig => {
    // Do not overwrite unsaved user edits.
    if (isDirty.value) return;
    if (newConfig) {
      isSyncingFromStore.value = true;
      isSyncingFromStore.value = false;
      markSynced();
    }
  },
  { immediate: true }
);

// Sync assistant name
watch(
  () => props.assistant,
  newVal => {
    // If user is editing (dirty), do not overwrite with store refresh.
    // Exception: assistant changed (different record) → allow sync.
    const nextId = Number(newVal?.id) || null;
    const currentId = Number(props.assistant?.id) || null;
    const assistantChanged = nextId && currentId && nextId !== currentId;
    if (isDirty.value && !assistantChanged) return;

    if (newVal?.name) {
      formData.name = newVal.name;
    }

    markSynced();
  },
  { immediate: true }
);

watch(
  () => currentDraftSnapshot(),
  nextSnap => {
    if (isSyncingFromStore.value) return;
    if (!lastSyncedSnapshot.value) return;
    const a = JSON.stringify(nextSnap);
    const b = JSON.stringify(lastSyncedSnapshot.value);
    isDirty.value = a !== b;
  },
  { deep: true }
);

const handleSave = async () => {
  isSubmitting.value = true;
  try {
    // Update AiLab assistant (name/config) if changed
    const assistantPayload = {};
    if (props.assistant.name !== formData.name) {
      assistantPayload.name = formData.name;
    }

    if (Object.keys(assistantPayload).length > 0) {
      await store.dispatch('ailabAssistants/update', {
        id: props.assistant.id,
        assistant: assistantPayload,
      });
    }

    useAlert('Settings saved successfully');
    // After a successful save, treat current values as synced baseline.
    markSynced();
  } catch (error) {
    useAlert(error?.message || 'Failed to save settings');
  } finally {
    isSubmitting.value = false;
  }
};

defineExpose({
  save: handleSave,
});
</script>

<template>
  <div class="general-tab fin-ai">
    <div v-if="isLoading" class="loading-state">
      <span class="i-lucide-loader-2 size-6 animate-spin" />
      <span>{{ t('AILAB.ASSISTANT_V2.GENERAL.LOADING') }}</span>
    </div>

    <div v-else class="general-tab__shell">
      <div class="fin-page-header-row">
        <div>
          <h1 class="heading o__size-1">Get Started</h1>
          <p class="fin-page-intro-copy mt-1">
            Set the core AI basics in one clean place, then continue into content,
            channels, and settings when you are ready.
          </p>
        </div>
      </div>

      <div class="fin-page-body">
        <section class="fin-page-hero general-hero">
          <div class="fin-page-hero__eyebrow">AI setup</div>
          <div class="fin-page-hero__title">
            Launch the assistant with a cleaner starting point.
          </div>
          <p class="fin-page-hero__copy">
            Use Quick Start to seed brand basics and official links. Keep the
            assistant name clear for your team, then continue into Train and Deploy
            once the foundation is ready.
          </p>
          <div class="fin-page-stats">
            <div v-for="card in setupOverviewCards" :key="card.label" class="fin-page-stat">
              <div class="fin-page-stat__label">{{ card.label }}</div>
              <div class="fin-page-stat__value">{{ card.value }}</div>
              <div class="fin-page-stat__note">{{ card.note }}</div>
            </div>
          </div>
        </section>

        <div class="general-layout">
          <section class="fin-page-surface general-panel">
            <div class="general-panel__header">
              <div>
                <h2 class="general-panel__title">Quick Start</h2>
                <p class="general-panel__copy">
                  Seed brand basics, welcome copy, and official links from one
                  onboarding flow.
                </p>
              </div>
            </div>
            <div class="general-panel__body">
              <QuickStartSetupCard :assistant="assistant" />
            </div>
          </section>

          <div class="general-side">
            <section class="fin-page-surface general-panel">
              <div class="general-panel__header">
                <div>
                  <h2 class="general-panel__title">
                    {{ t('AILAB.ASSISTANT_V2.GENERAL.CARDS.BASIC_INFO.TITLE') }}
                  </h2>
                  <p class="general-panel__copy">
                    Keep the assistant name tidy before the rest of the team starts
                    using it.
                  </p>
                </div>
              </div>

              <div class="general-panel__body">
                <div class="form-group">
                  <label class="form-label">
                    {{ t('AILAB.ASSISTANT_V2.GENERAL.FIELDS.NAME.LABEL') }}
                    <span class="required">*</span>
                  </label>
                  <input
                    v-model="formData.name"
                    type="text"
                    class="form-input"
                    :placeholder="
                      t('AILAB.ASSISTANT_V2.GENERAL.FIELDS.NAME.PLACEHOLDER')
                    "
                    maxlength="30"
                  />
                  <small class="form-hint">{{
                    t('AILAB.ASSISTANT_V2.GENERAL.HINTS.MAX_30')
                  }}</small>
                </div>
              </div>
            </section>

            <section class="fin-page-surface general-panel">
              <div class="general-panel__header">
                <div>
                  <h2 class="general-panel__title">Where to continue</h2>
                  <p class="general-panel__copy">
                    Move into the next workspace based on what you want to improve.
                  </p>
                </div>
              </div>

              <div class="general-actions">
                <button type="button" class="btn o__secondary general-route-btn" @click="openTrainContentTab">
                  Open Train Content
                </button>
                <button type="button" class="btn o__secondary general-route-btn" @click="openDeployTab">
                  Open Channels & Inboxes
                </button>
                <button type="button" class="btn o__secondary general-route-btn" @click="openSettingsTab">
                  Open Settings
                </button>
              </div>
            </section>

            <div class="form-actions">
              <button
                type="button"
                class="btn o__primary general-save-btn"
                :disabled="isSubmitting || uiFlags.isUpdatingConfig"
                @click="handleSave"
              >
                <span
                  v-if="isSubmitting || uiFlags.isUpdatingConfig"
                  class="i-lucide-loader-2 size-4 animate-spin"
                />
                <span v-else class="i-lucide-save size-4" />
                {{ t(`${BASE_KEY}.ACTIONS.SAVE_CHANGES`) }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.general-tab {
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.general-tab__shell {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 320px;
  color: var(--fin-muted);
}

.general-hero {
  margin-bottom: 20px;
}

.general-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(320px, 0.95fr);
  gap: 20px;
  align-items: start;
}

.general-side {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.general-panel {
  padding: 20px;
}

.general-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.general-panel__title {
  font-size: 18px;
  line-height: 24px;
  font-weight: 700;
  color: #0f172a;
}

.general-panel__copy {
  margin-top: 6px;
  font-size: 13px;
  line-height: 20px;
  color: var(--fin-muted);
}

.general-panel__body {
  margin-top: 18px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-label {
  font-size: 12px;
  line-height: 16px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.required {
  color: #dc2626;
  margin-left: 4px;
}

.form-input {
  width: 100%;
  padding: 11px 13px;
  font-size: 13px;
  line-height: 1.5;
  color: #111827;
  background: #ffffff;
  border: 1px solid var(--fin-neutral-border);
  border-radius: 10px;
  transition: all 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #93c5fd;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.08);
}

.form-input:hover {
  border-color: #cbd5e1;
}

.form-input::placeholder {
  color: #9ca3af;
}

.form-hint {
  font-size: 12px;
  line-height: 18px;
  color: var(--fin-muted);
}

.general-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.general-route-btn {
  justify-content: center;
  width: 100%;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
}

.general-save-btn {
  min-width: 180px;
  justify-content: center;
}

@media (max-width: 1100px) {
  .general-layout {
    grid-template-columns: 1fr;
  }
}
</style>
